import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { TableColumnDetailsMap } from '@rio-cloud/rio-uikit/components/table/TableSettingsDialog.types';
import { SortDirection, type SortDirectionType } from '@rio-cloud/rio-uikit/SortDirection';
import TableViewToggles, { type TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';

import { fastCloneDeep } from '../../utils/utils';
import { summaryActions } from '../summary/summaryActions';
import { tableConfig } from './tableConfig';

export type SortingValue = { sortBy: string; sortDir: SortDirectionType };
export type TableColumnsSettings = {
    columnOrder: string[];
    hiddenColumns: string[];
    columnsDetails: TableColumnDetailsMap;
};

export type TableState = {
    search: string;
    sortBy: string;
    sortDir: SortDirectionType;
    chunks: number;
    columns: TableColumnsSettings;
    activityFilter: string[];
    assetFilter: string[];
    notificationFilter: string[];
    showTableSettings: boolean;
    viewType: TableViewTogglesViewType;
};

export const defaultTableState: TableState = {
    search: '',
    sortBy: tableConfig.defaultSortKey,
    sortDir: SortDirection.ASCENDING,
    chunks: 1,
    columns: {
        columnOrder: tableConfig.defaultColumnOrder,
        hiddenColumns: tableConfig.defaultHiddenColumns,
        columnsDetails: tableConfig.defaultColumnsDetails,
    },
    activityFilter: [],
    assetFilter: [],
    notificationFilter: [],
    showTableSettings: false,
    viewType: TableViewToggles.VIEW_TYPE_TABLE,
};

export const tableSlice = createSlice({
    name: 'table',
    initialState: defaultTableState,
    reducers: {
        tableSearchChanged: (state, action: PayloadAction<string>) => ({
            ...state,
            search: action.payload,
        }),
        tableColumnsChanged: (state, action: PayloadAction<TableColumnsSettings>) => ({
            ...state,
            columns: fastCloneDeep(action.payload),
        }),
        tableSettingsDialogToggled: state => ({ ...state, showTableSettings: !state.showTableSettings }),
        tableViewTypeChanged: (state, action: PayloadAction<TableViewTogglesViewType>) => ({
            ...state,
            viewType: action.payload,
        }),
        chunkCounterChanged: (state, action: PayloadAction<number>) => ({
            ...state,
            chunks: action.payload,
        }),
        sortingPropsChanged: (state, action: PayloadAction<SortingValue>) => ({
            ...state,
            sortBy: action.payload.sortBy,
            sortDir: action.payload.sortDir,
        }),
    },
    extraReducers: builder => {
        builder.addCase(summaryActions.activityFilterChanged, (state, action: PayloadAction<string[]>) => {
            state.activityFilter = action.payload || defaultTableState.activityFilter;
        });
        builder.addCase(summaryActions.assetTypeFilterChanged, (state, action: PayloadAction<string[]>) => {
            state.assetFilter = action.payload || defaultTableState.assetFilter;
        });
        builder.addCase(summaryActions.notificationFilterChanged, (state, action: PayloadAction<string[]>) => {
            state.notificationFilter = action.payload || defaultTableState.notificationFilter;
        });
    },
});

export const {
    tableSearchChanged,
    tableColumnsChanged,
    tableSettingsDialogToggled,
    tableViewTypeChanged,
    chunkCounterChanged,
    sortingPropsChanged,
} = tableSlice.actions;

export default tableSlice.reducer;
